// src/components/SoundEffectsManager.jsx
// Sits inside <LiveKitRoom> and plays join/leave sounds when
// remote participants connect or disconnect.

import { useEffect, useRef } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';
import { useSoundEffects } from '@/hooks/useSoundEffects';

const SoundEffectsManager = () => {
  const room = useRoomContext();
  const { playJoin, playLeave } = useSoundEffects();
  const initialParticipantsLoaded = useRef(false);

  useEffect(() => {
    if (!room) return;

    // Skip the initial burst of ParticipantConnected events that fire
    // when you first join a room with existing participants.
    const skipTimer = setTimeout(() => {
      initialParticipantsLoaded.current = true;
    }, 3000);

    const handleParticipantConnected = (participant) => {
      // Only play after the initial load phase and only for remote participants
      if (!initialParticipantsLoaded.current) return;
      if (participant.isLocal) return;

      console.log(`[Sound] Participant joined: ${participant.identity}`);
      playJoin();
    };

    const handleParticipantDisconnected = (participant) => {
      if (participant.isLocal) return;

      console.log(`[Sound] Participant left: ${participant.identity}`);
      playLeave();
    };

    room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);

    return () => {
      clearTimeout(skipTimer);
      room.off(RoomEvent.ParticipantConnected, handleParticipantConnected);
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
    };
  }, [room, playJoin, playLeave]);

  return null; // renderless component
};

export default SoundEffectsManager;
